import { useEffect, useState } from 'react';

import { TRBLSchedule } from '@helpers/FormalOffers/formalOffers.types';
import { getIsRBLOffer } from '@helpers/FormalOffers/getIsRBLOffer';
import { fetchGetMcaEstimatedSchedule } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';

import { EstimatedRepayments } from './EstimatedRepayments/EstimatedRepayments';
import {
    convertSweepToDecimal,
    convertSweepToPercentage
} from './RBLOfferConfiguration.helpers';
import rblStyles from './RBLOfferConfigurationView.module.css';
import { RblSweepSelect } from './rblSweepSelect/rblSweepSelect';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import Fetching from '../../../../ui/fetching/fetching';
import { FormalOffer } from '../../../MyOffers.types';

export const RBLOfferConfiguration = ({
    selectedOffer,
    drawdownAmount,
    updateRblSchedule
}: {
    selectedOffer: FormalOffer;
    drawdownAmount: number;
    updateRblSchedule: (next: TRBLSchedule) => void;
}) => {
    const stateKey = useStateKey();

    const [sweep, setSweep] = useState(0.0);
    const [sweepVals, setSweepVals] = useState({
        absMin: 0,
        absMax: 0,
        min: 0,
        max: 0
    });

    const {
        data: schedule,
        isFetching: scheduleLoading,
        error: scheduleError
    } = useQuery({
        queryKey: [
            'mcaSchedule',
            stateKey,
            selectedOffer?.offer_id!,
            drawdownAmount,
            sweep
        ],
        queryFn: () => {
            return fetchGetMcaEstimatedSchedule({
                stateKey: stateKey,
                offerId: selectedOffer?.offer_id!,
                query: {
                    cash_amount:
                        drawdownAmount > 0 ? drawdownAmount : undefined,
                    sweep_fraction:
                        sweep > 0 ? convertSweepToDecimal(sweep) : undefined
                }
            });
        },
        enabled: Boolean(selectedOffer) && getIsRBLOffer(selectedOffer!),
        keepPreviousData: true
    });

    useEffect(() => {
        updateRblSchedule({
            schedule: schedule,
            scheduleLoading: scheduleLoading
        });
    }, [schedule, scheduleLoading, updateRblSchedule]);

    if (scheduleError) {
        return <FallbackErrorModal />;
    }

    // // Set intial sweep values
    if (sweep === 0 && schedule) {
        const initialSweepMin = convertSweepToPercentage(
            schedule.data.summary.sweep.minimum_sweep_fraction
        );
        const initialSweepMax = convertSweepToPercentage(
            schedule.data.summary.sweep.maximum_sweep_fraction
        );
        setSweep(initialSweepMin);
        setSweepVals({
            absMin: 0.1,
            absMax: initialSweepMax,
            min: initialSweepMin,
            max: initialSweepMax
        });
    }

    return (
        <section>
            <RblSweepSelect
                key={selectedOffer!.offer_id}
                offer={selectedOffer!}
                drawdownAmount={drawdownAmount}
                sweep={sweep!}
                setSweep={setSweep!}
                schedule={schedule?.data!}
                sweepVals={sweepVals!}
                setSweepVals={setSweepVals!}
            />

            {schedule && (
                <EstimatedRepayments
                    className={rblStyles.estimatedSummary}
                    schedule={schedule!}
                    loading={scheduleLoading}
                />
            )}

            <p className={rblStyles.legalText}>
                <FormattedMessage
                    id="_.rblOffer_configuration.legal_text"
                    defaultMessage="Repayments are automatically collected by eBay and we estimate it will take <fetching><bold>{period} months</bold></fetching> to repay the loan. The actual duration will depend upon revenue. Half of the expected repayment is due as a minimum repayment every 60 days. If the minimum repayment is not met, we will collect the difference by direct debit."
                    values={{
                        period: schedule?.data.estimated_schedule
                            .estimated_time_to_repay_in_months,
                        bold: (chunks) => <strong>{chunks}</strong>,
                        fetching: (chunks) => (
                            <Fetching isFetching={scheduleLoading}>
                                {chunks}
                            </Fetching>
                        )
                    }}
                />
            </p>
        </section>
    );
};
